// apps/web/src/lib/useTopmostEscape.ts
// Escape-to-close for modals, scoped to the TOPMOST open modal only. All modal
// keydown listeners sit on `document` and fire in registration order, so a naive
// per-modal handler makes one Escape press close nested modals AND their parents
// (losing the user's work). A shared mount-order stack fixes this: only the modal
// currently on top of the stack responds to Escape.
import { useEffect, useRef } from "react";

const stack: symbol[] = [];

export function useTopmostEscape(onClose: () => void): void {
  // Keep the latest callback without re-running the mount-only effect.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    const id = Symbol("modal");
    stack.push(id);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (stack[stack.length - 1] !== id) return; // not the topmost modal
      e.preventDefault();
      onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      const i = stack.indexOf(id);
      if (i !== -1) stack.splice(i, 1);
    };
  }, []);
}
